import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api/axios";
import ledger from "../../../api/ledger";

const LABEL = {
  display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: "var(--mly-text-muted)", marginBottom: 8, textTransform: "uppercase",
};

const INPUT = {
  width: "100%", padding: "11px 14px", borderRadius: 10,
  border: "1.5px solid var(--mly-border)", fontSize: 14, boxSizing: "border-box", outline: "none",
};

const BTN = {
  fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, padding: "8px 14px", borderRadius: 8,
  border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text)", cursor: "pointer",
};

const SECTION_TITLE = {
  fontSize: 15, fontWeight: 700, color: "var(--mly-text)",
  paddingBottom: 10, marginBottom: 20, borderBottom: "1px solid var(--mly-border-soft)",
  display: "flex", alignItems: "center", gap: 8,
};

/* ── Per-store overrides ───────────────────────────────────────────────
   Settings are ONE global set that every store inherits. A store may pin
   its own value for the fields below; anything it has not pinned keeps
   following the global row, so an estate-wide price change stays a single
   edit. Three kinds of field live on this page:
     • store-owned  — hours, address, delivery charges: they belong to the
                      Store record and are edited on /admin/stores;
     • global-only  — the Reelo credentials: one set for the whole estate;
     • overridable  — everything below, the operational knobs.
   The list is repeated here (not derived) so a field the API starts
   returning as overridden can never sneak a store-owned value into a
   per-store patch.

   millingFeePerKg is deliberately NOT here. It is the legacy single fee
   this form mirrors from the flour box, and it doubles as the fallback
   the mobile API uses for whichever mill has no dedicated fee
   (millingFeePerKgFor). Pinning it per store would set that store's
   masala fee to the flour fee whenever the global masala fee is unset,
   so the store patch carries the dedicated fees only.                   */
const OVERRIDABLE = [
  "codEnabled",
  "codForMillingEnabled",
  "showGstOnBill",
  "millingFeePerKgFlour",
  "millingFeePerKgMasala",
  "roastChargePerKg",
  "roastCapacityFactor",
  "taxMode",
];

/* Everything else this page used to pin is refused by the per-store PUT,
   and the refusal aborts the WHOLE patch on the first offending key:
   hours, address, store name and the three delivery money fields belong to
   the store record ("edit it on the Stores page").

   Migration 0036 gave the rest of what this page shows real columns, so gstin,
   support*, grievance*, minWeightGrams, weightStepGrams and liveStream* now
   travel with the estate save. The three LISTS -- cameras, delivery windows
   and the home-screen categories -- are tables rather than columns, so they go
   to /admin/settings/{cameras,delivery-windows,featured-categories} instead.

   Of the six above, only the two per-mill fees are NULLABLE columns — so
   they are the only two a branch can say "not overridden" about, the only
   two DELETE .../{field} accepts, and the only two the server can report
   back as overridden. The Inherited/Overridden chip on the other four is
   the operator's own intent, not the server's answer. */
const REVERTIBLE = ["millingFeePerKgFlour", "millingFeePerKgMasala"];


/* _meta.overriddenFields comes back in the PAISE spelling */
const OVERRIDDEN_ALIASES = {
  millingFeePerKgFlourPaise: "millingFeePerKgFlour",
  millingFeePerKgMasalaPaise: "millingFeePerKgMasala",
};

const normaliseOverridden = (list) =>
  (Array.isArray(list) ? list : []).map((f) => OVERRIDDEN_ALIASES[String(f)] || String(f));

/* The server speaks paise; every ₹ box on this form is rupees. */
const rupees = (paise, fallback) => {
  const n = Number(paise);
  return paise === null || paise === undefined || !Number.isFinite(n) ? fallback : String(n / 100);
};

const CHIP_ROW = {
  display: "flex", alignItems: "center", gap: 8,
  marginBottom: 6, flexWrap: "wrap", minHeight: 20,
};

const CHIP = {
  fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase",
  padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap",
};

const CHIP_INHERITED  = { ...CHIP, background: "var(--mly-surface-3)", color: "var(--mly-text-muted)", border: "1px solid var(--mly-border)" };
const CHIP_OVERRIDDEN = { ...CHIP, background: "var(--mly-accent-soft)", color: "var(--mly-heading)", border: "1px solid var(--mly-border)" };
const CHIP_STORE_OWNED = { ...CHIP, background: "var(--mly-surface-2)", color: "var(--mly-accent)", border: "1px solid var(--mly-border)" };
const CHIP_NOT_STORED  = { ...CHIP, background: "var(--mly-surface-3)", color: "var(--mly-text-muted)", border: "1px solid var(--mly-border)" };

const LINK_BTN = {
  border: "none", background: "none", padding: 0, fontSize: 11, fontWeight: 700,
  color: "var(--mly-heading)", cursor: "pointer", textDecoration: "underline",
};

/* a disabled fieldset greys the control AND takes it out of the tab order,
   which a pointer-events trick would not */
const FIELDSET_RESET = {
  display: "block", border: 0, padding: 0, margin: 0, minWidth: 0,
};
const INHERITED_BOX = { ...FIELDSET_RESET, opacity: 0.55 };

/* ── Tax mode ──────────────────────────────────────────────────────────
   The catalogue price either IS the final price or it is not, and this
   choice is what decides which. It is rendered as two labelled options
   rather than a dropdown on purpose: a dropdown shows one word and hides
   the consequence, and the consequence here is "every customer pays more".
   The copy states the outcome for the CUSTOMER, not the accounting rule —
   GST itself is never shown to customers (2026-08-21 decision), so the
   only visible effect of this setting is the price they are charged.    */
const TAX_MODE_CHOICES = [
  {
    value: "inclusive",
    title: "Listed price is the final price",
    detail: "GST is inside it. The customer pays exactly the number shown on the product.",
    note: "This is how every existing order was placed.",
  },
  {
    value: "exclusive",
    title: "GST is added at checkout",
    detail: "Customers pay more than the listed price. No price is edited — every bill simply grows by the tax.",
    note: "Switching here changes what customers are charged from the next order onwards.",
  },
];

/* ── Reelo credentials ─────────────────────────────────────────────────
   TODO(cutover): NOTHING STORES THESE ANY MORE. reeloVendorId,
   reeloMerchantId and reeloCustomerKey used to be saved from this page
   through the Node settings PUT. On the ledger service they are
   connection-level and refused by name by both settings PUTs, and the one
   endpoint that accepts them — PUT /admin/loyalty/credentials — validates
   the body in full and then answers 501 settings_not_storable, because
   shop.CfgStoreSettings has no reelo_* columns. They are only READ, resolved
   from the server's REELO_* environment, by GET /admin/loyalty/config.
   So the editor that used to sit at the bottom of this page was removed
   rather than left posting into a refusal, and the copy beside the Loyalty
   & Reelo link says the values are environment-set — pointing an operator
   at a page that cannot save them either would only move the dead end. */

const SCOPE_PILL = (active) => ({
  display: "inline-flex", alignItems: "center", gap: 7,
  padding: "7px 14px", borderRadius: 999, cursor: "pointer",
  border: `1.5px solid ${active ? "var(--milliyo-forest)" : "var(--mly-border)"}`,
  background: active ? "var(--milliyo-forest)" : "var(--mly-surface)",
  color: active ? "#E8C96A" : "var(--mly-text-soft)",
  fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
});

const COUNT_BADGE = (active) => ({
  background: active ? "#C9952A" : "var(--mly-accent-soft)",
  color: active ? "#fff" : "var(--mly-heading)",
  borderRadius: 999, minWidth: 18, height: 18, padding: "0 6px",
  fontSize: 11, lineHeight: "18px", textAlign: "center", fontWeight: 700,
});

/* Wraps one overridable field (or a pair that only makes sense together).
   Renders nothing of its own on the global set, so "All stores" mode is the
   page exactly as it always was. */
const Ovr = ({ storeMode, on, busy, onOverride, onRevert, gone, why, children }) => {
  /* a field the ledger service has no column for: shown, disabled, and
     labelled as such in BOTH scopes — it saves nowhere, not just here */
  if (gone) {
    return (
      <div>
        <div style={CHIP_ROW}>
          <span style={CHIP_NOT_STORED}>Not stored yet</span>
          <span style={{ fontSize: 11, color: "var(--mly-text-muted)" }}>{why}</span>
        </div>
        <fieldset disabled style={INHERITED_BOX}>{children}</fieldset>
      </div>
    );
  }
  if (!storeMode) return <>{children}</>;
  return (
    <div>
      <div style={CHIP_ROW}>
        <span style={on ? CHIP_OVERRIDDEN : CHIP_INHERITED}>{on ? "Overridden" : "Inherited"}</span>
        {on ? (
          <button type="button" style={LINK_BTN} disabled={busy} onClick={onRevert}>
            {busy ? "Reverting..." : "Revert to inherited"}
          </button>
        ) : (
          <button type="button" style={LINK_BTN} onClick={onOverride}>Override for this store</button>
        )}
      </div>
      {on ? children : <fieldset disabled style={INHERITED_BOX}>{children}</fieldset>}
    </div>
  );
};

const gone = (why) => ({ gone: true, why });

/* "<base>/<path>/index.m3u8", slashes normalised. The same shape the admin
   Live View builds its tiles from -- a camera whose URL is composed any other
   way here would play on this screen's Test button and nowhere else. */
const joinHlsUrl = (base, path) =>
  `${String(base || "").replace(/\/+$/, "")}/${String(path || "").replace(/^\/+/, "")}/index.m3u8`;

/* A stream path from the camera's name: "Flour Mill 1" -> "flour-mill-1".
   Falls back to cam<n> so a camera added before it is named still gets a
   working URL rather than one ending in a bare slash. */
const streamSlug = (name, index) => {
  const slug = String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || `cam${index + 1}`;
};

/* Wraps a field that lives on the Store record itself — shown so the page
   still reads as one screen, but only editable where it is owned. */
const StoreOwned = ({ storeMode, children }) => {
  if (!storeMode) return <>{children}</>;
  return (
    <div>
      <div style={CHIP_ROW}>
        <span style={CHIP_STORE_OWNED}>Store field</span>
        <Link to="/admin/stores" style={LINK_BTN}>Edit on Stores</Link>
      </div>
      <fieldset disabled style={INHERITED_BOX}>{children}</fieldset>
    </div>
  );
};

const StoreSettings = () => {
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState("");

  const [storeName,        setStoreName]        = useState("");
  const [timezone,         setTimezone]         = useState("Asia/Kolkata");
  const [gstin,            setGstin]            = useState("");
  /* 0061: the HSN/SAC a milled blend and the delivery charge are invoiced
     under -- the two kinds of line with no product to carry one. Estate-wide. */
  const [milledHsn,        setMilledHsn]        = useState("");
  const [deliveryHsn,      setDeliveryHsn]      = useState("");
  /* printed on the bill: the outlet's FSSAI licence, the UPI handle its QR
     code pays into, and the shop's own closing line */
  const [fssaiNo,          setFssaiNo]          = useState("");
  const [upiVpa,           setUpiVpa]           = useState("");
  const [receiptFooter,    setReceiptFooter]    = useState("");

  /* "inclusive" | "exclusive" — see TAX_MODE_CHOICES */
  const [taxMode,          setTaxMode]          = useState("inclusive");
  /* customer-facing support contact — the apps read these from settings */
  const [supportPhone,     setSupportPhone]     = useState("");
  const [supportWhatsapp,  setSupportWhatsapp]  = useState("");
  const [supportEmail,     setSupportEmail]     = useState("");
  const [grievanceName,    setGrievanceName]    = useState("");
  const [grievanceEmail,   setGrievanceEmail]   = useState("");
  const [grievancePhone,   setGrievancePhone]   = useState("");
  const [addressLine,      setAddressLine]      = useState("");
  const [openTime,         setOpenTime]         = useState("");
  const [closeTime,        setCloseTime]        = useState("");
  const [pickupEnabled,    setPickupEnabled]    = useState(true);
  const [deliveryEnabled,  setDeliveryEnabled]  = useState(true);
  const [millingFeeFlour,  setMillingFeeFlour]  = useState("");
  const [millingFeeMasala, setMillingFeeMasala] = useState("");
  const [roastChargePerKg, setRoastChargePerKg] = useState("");
  const [minWeightGrams,   setMinWeightGrams]   = useState("");
  const [weightStepGrams,  setWeightStepGrams]  = useState("");
  const [roastCapacityFactor, setRoastCapacityFactor] = useState("1.5");
  const [onlineOrderStartTime, setOnlineOrderStartTime] = useState("09:00");
  const [onlineOrderEndTime,   setOnlineOrderEndTime]   = useState("21:00");
  const [liveStreamEnabled, setLiveStreamEnabled] = useState(false);
  const [liveStreamUrl,     setLiveStreamUrl]     = useState("");
  const [cameras,           setCameras]           = useState([]); // {name,url,millType,showToCustomer,enabled}
  /* Read-only: the HLS base the server resolves to, stored column or env
     fallback. Not saved -- it is what the URL boxes below are prefilled from. */
  const [streamHlsBase,     setStreamHlsBase]     = useState("");
  /* Per-row reachability check: { [index]: "checking" | "ok" | "fail" }. */
  const [camProbe,          setCamProbe]          = useState({});
  const [minOrderValue,     setMinOrderValue]     = useState("200");
  const [freeDeliveryAbove, setFreeDeliveryAbove] = useState("500");
  const [deliveryCharge,    setDeliveryCharge]    = useState("40");
  const [deliveryWindows,   setDeliveryWindows]   = useState([]); // {orderFrom,orderTo,deliverBy}
  const [categories,        setCategories]        = useState([]);
  /* app-home circle categories — any number, in click order */
  const [featuredIds,       setFeaturedIds]       = useState([]);
  /* payments */
  const [codForMillingEnabled, setCodForMillingEnabled] = useState(true);
  const [codEnabled,           setCodEnabled]           = useState(true);
  const [showGstOnBill,        setShowGstOnBill]        = useState(true);
  /* loyalty (Reelo) is inspected on its own page — /admin/loyalty. The outlet
     credentials are connection-level on the ledger service and refused by name
     by both settings PUTs; they are READ by GET /admin/loyalty/config and
     stored by nothing (PUT /admin/loyalty/credentials answers 501). Nothing
     about them is mirrored into this component any more. */

  /* scope: null = the global defaults, otherwise the store being edited */
  const [stores,     setStores]     = useState([]);
  const [storeId,    setStoreId]    = useState(null);

  /* The logo printed at the top of the bill. It is not a settings COLUMN --
     it is an image the printer needs as dots, so it lives with the other print
     assets and is uploaded through the print service. It is offered here
     because this is the page somebody opens when they want to change what the
     bill looks like. */
  const [billLogo,     setBillLogo]     = useState(null);
  const [billLogoBusy, setBillLogoBusy] = useState(false);

  const loadBillLogo = useCallback(async () => {
    try {
      const { data } = await ledger.get("/admin/print/layout", { params: { storeId: storeId || undefined } });
      setBillLogo(data.logo || null);
    } catch { setBillLogo(null); }
  }, [storeId]);

  const uploadBillLogo = async (file) => {
    if (!file) return;
    setBillLogoBusy(true);
    try {
      const png = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(new Error("That file could not be read."));
        r.readAsDataURL(file);
      });
      const { data } = await ledger.post("/admin/print/logo", { storeId: storeId || undefined, png });
      setBillLogo({ widthDots: data.widthDots, heightDots: data.heightDots });
      toast.success(data.message || "Logo saved");
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save that logo");
    } finally { setBillLogoBusy(false); }
  };

  const removeBillLogo = async () => {
    setBillLogoBusy(true);
    try {
      await ledger.delete("/admin/print/logo", { params: { storeId: storeId || undefined } });
      setBillLogo(null);
      toast.success("Logo removed");
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not remove the logo");
    } finally { setBillLogoBusy(false); }
  };
  /* whether this admin may edit the estate-wide row at all — the server's
     answer (stores-overview meta), not a guess from how many stores exist */
  const [canEditGlobal, setCanEditGlobal] = useState(true);
  /* every field this store pins, including ones only clicked and not saved */
  const [overridden, setOverridden] = useState([]);
  /* the subset the API already stores — a DELETE is only worth firing for those */
  const [serverOverridden, setServerOverridden] = useState([]);
  const [reverting,  setReverting]  = useState("");
  /* a scope whose load failed still shows the PREVIOUS scope's values while
     pointing at the new one — saving then would copy one store's settings
     onto another, or onto the global row. Block the save until a load lands. */
  const [loadFailed, setLoadFailed] = useState(false);
  /* the global row, kept aside so reverting can put the inherited value back
     without discarding whatever else is half-typed on the form */
  const globalRef = useRef(null);

  const storeMode = !!storeId;
  const activeStore = stores.find((s) => String(s.storeId) === String(storeId));

  /* `only` limits the apply to a few fields — used when one override is
     reverted and just that field must fall back to the global value.

     Migration 0036 gave timezone, the GSTIN, the support contacts, the
     weights and the stream settings real columns, and 0044 added the three
     the printed bill needs, so all of them are loaded below. The cameras,
     the prepacked delivery windows and the featured categories are their own
     collections and load separately; the reelo* values are read-only here and
     are set on the Loyalty screen. */
  const applySettings = useCallback((s, only) => {
    const want = (f) => !only || only.includes(f);
    if (want("storeName")) setStoreName(s.storeName || "");
    /* anything other than the two known modes is treated as inclusive — the
       same fallback the server applies when it prices, so the form can
       never show a mode the pricing code would not honour */
    if (want("taxMode")) setTaxMode(s.taxMode === "exclusive" ? "exclusive" : "inclusive");
    if (want("addressLine")) setAddressLine(s.addressLine || "");

    /* Everything below is read back because it is written above. It was the
       other way round for one release: the save sent gstin, the support
       contacts, the weights, the stream URL and the grievance officer, and the
       load set none of them -- so the boxes were empty on arrival and Save
       wrote that emptiness over the real values. The GSTIN on every printed
       invoice was one visit to this screen away from being erased. */
    if (want("timezone")) setTimezone(s.timezone || "Asia/Kolkata");
    if (want("gstin")) setGstin(s.gstin || "");
    if (want("milledHsn")) setMilledHsn(s.milledHsn || "");
    if (want("deliveryHsn")) setDeliveryHsn(s.deliveryHsn || "");
    if (want("fssaiNo")) setFssaiNo(s.fssaiNo || "");
    if (want("upiVpa")) setUpiVpa(s.upiVpa || "");
    if (want("receiptFooter")) setReceiptFooter(s.receiptFooter || "");
    if (want("supportPhone")) setSupportPhone(s.supportPhone || "");
    if (want("supportWhatsapp")) setSupportWhatsapp(s.supportWhatsapp || "");
    if (want("supportEmail")) setSupportEmail(s.supportEmail || "");
    if (want("grievanceOfficerName")) setGrievanceName(s.grievanceOfficerName || "");
    if (want("grievanceEmail")) setGrievanceEmail(s.grievanceEmail || "");
    if (want("grievancePhone")) setGrievancePhone(s.grievancePhone || "");
    if (want("minWeightGrams")) setMinWeightGrams(String(s.minWeightGrams ?? 250));
    if (want("weightStepGrams")) setWeightStepGrams(String(s.weightStepGrams ?? 250));
    if (want("liveStreamEnabled")) setLiveStreamEnabled(!!s.liveStreamEnabled);
    if (want("liveStreamUrl")) setLiveStreamUrl(s.liveStreamUrl || "");
    /* The base the server will ACTUALLY serve from: the stored hlsBaseUrl, or
       the HLS_BASE_URL in its environment when that column is blank. Response
       only -- it is not a field, it is what the fields default to.

       Shown and prefilled from, because a blank box over a working environment
       default is a trap: it reads as "nothing is configured", so somebody types
       a URL to fix it, and that guess then OVERRIDES the value that was working.
       The same base is what the admin Live View builds its tiles from. */
    if (want("streamHlsBase")) setStreamHlsBase(s.streamHlsBase || "");
    if (want("openTime")) setOpenTime(s.openTime || "");
    if (want("closeTime")) setCloseTime(s.closeTime || "");
    if (want("pickupEnabled")) setPickupEnabled(!!s.pickupEnabled);
    if (want("deliveryEnabled")) setDeliveryEnabled(!!s.deliveryEnabled);
    /* MONEY ARRIVES IN PAISE — every ₹ box divides by 100. The two per-mill
       fees are nullable and fall back to the shared fee. */
    const flourFee = (s.millingFeePerKgFlourPaise !== undefined && s.millingFeePerKgFlourPaise !== null)
      ? s.millingFeePerKgFlourPaise : s.millingFeePerKgPaise;
    const masalaFee = (s.millingFeePerKgMasalaPaise !== undefined && s.millingFeePerKgMasalaPaise !== null)
      ? s.millingFeePerKgMasalaPaise : s.millingFeePerKgPaise;
    if (want("millingFeePerKgFlour") || want("millingFeePerKg")) {
      setMillingFeeFlour(rupees(flourFee, ""));
    }
    if (want("millingFeePerKgMasala")) setMillingFeeMasala(rupees(masalaFee, ""));
    if (want("roastChargePerKg")) setRoastChargePerKg(rupees(s.roastChargePerKgPaise, ""));
    if (want("roastCapacityFactor")) {
      setRoastCapacityFactor(
        s.roastCapacityFactor !== undefined && s.roastCapacityFactor !== null
          ? String(s.roastCapacityFactor) : "1.5"
      );
    }
    if (want("onlineOrderStartTime")) setOnlineOrderStartTime(s.onlineOrderStartTime || "09:00");
    if (want("onlineOrderEndTime")) setOnlineOrderEndTime(s.onlineOrderEndTime || "21:00");
    if (want("minOrderValue")) setMinOrderValue(rupees(s.minOrderPaise, "200"));
    if (want("freeDeliveryAbove")) setFreeDeliveryAbove(rupees(s.freeDeliveryAbovePaise, "500"));
    if (want("deliveryCharge")) setDeliveryCharge(rupees(s.deliveryFeePaise, "40"));
    if (want("codForMillingEnabled")) setCodForMillingEnabled(s.codForMillingEnabled !== false);
    if (want("codEnabled")) setCodEnabled(s.codEnabled !== false);
    if (want("showGstOnBill")) setShowGstOnBill(s.showGstOnBill !== false);
  }, []);

  /* a single-store estate never reaches the multi-store endpoints, and an
     estate whose API predates them simply keeps the old single-set page.
     Returns what it loaded so boot can pick a scope from it without waiting
     on a state update it has not been re-rendered for yet. */
  const loadOverview = useCallback(async () => {
    try {
      /* the one read on this page that still answers { data, meta } */
      const res = await api.get("/admin/store-settings/overview");
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      /* an API that predates the flag keeps today's behaviour — assume the
         estate row is editable and let the 403 speak if it is not */
      /* NOTE: row.overriddenCount counts only the two nullable per-mill fee
         columns (meta.overriddenCountIsPartial), so the badge on each pill
         tops out at 2 and under-reports every other pinned field. */
      const may = res.data?.meta?.canEditGlobal;
      const canGlobal = may === undefined ? true : !!may;
      setStores(list);
      setCanEditGlobal(canGlobal);
      return { list, canGlobal };
    } catch (err) {
      setStores([]);
      return { list: [], canGlobal: true };
    }
  }, []);

  const loadSettings = useCallback(async (id) => {
    try {
      if (id) {
        const [resolvedRes, globalRes] = await Promise.all([
          api.get(`/admin/store-settings/resolved?storeId=${encodeURIComponent(id)}`),
          globalRef.current ? Promise.resolve(null) : api.get("/admin/store-settings"),
        ]);
        /* both replies ARE the settings payload — no { data } envelope — and
           _meta rides inside the resolved one rather than beside it */
        if (globalRes) globalRef.current = globalRes.data || null;
        const body = resolvedRes.data;
        const meta = body?._meta || {};
        if (body) applySettings(body);
        /* overriddenFields can only ever name the two per-mill fees
           (_meta.overriddenFieldsComplete is false), so every other pinned
           field reads as Inherited until its column can hold a NULL */
        const fields = normaliseOverridden(meta.overriddenFields);
        setOverridden(fields);
        setServerOverridden(fields);
      } else {
        const res = await api.get("/admin/store-settings");
        const s = res.data;
        globalRef.current = s || null;
        if (s) applySettings(s);
        setOverridden([]);
        setServerOverridden([]);
      }
      setLoadFailed(false);
    } catch (err) {
      /* drop the override list with the values it described — a stale list
         against a new scope is what turns a failed load into a bad write.
         A 422 not_configured here is a broken install (the estate defaults
         row is missing), not an empty first read. */
      setOverridden([]);
      setServerOverridden([]);
      setLoadFailed(true);
      const msg = err.response?.data?.message || "Failed to load store settings";
      setFormError(`${msg}. Reload the page before saving — the form still shows the previous selection.`);
      toast.error(msg);
    }
  }, [applySettings]);

  useEffect(() => { loadBillLogo(); }, [loadBillLogo]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        /* still wrapped: { data, pagination }. This only feeds the featured
           category picker, which has nowhere to save — see its TODO below. */
        const res = await api.get("/admin/catalog/categories?limit=100");
        const list = res.data?.data || [];
        setCategories(list.filter((c) => c.isActive !== false));
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load categories");
      }
    };
    /* THE SCOPE IS DECIDED BEFORE THE FIRST LOAD, not after it.
       An admin who may not edit the estate row has exactly one scope they
       can save — their own store — and landing them on "All stores" showed
       them values they are not allowed to write behind a Save button that
       answers 403 and tells them to use their store's settings, with no
       control on the page to select it. So the overview comes first and its
       answer chooses the scope. A superadmin is unaffected: canGlobal is
       true, the initial scope stays null, and the page opens exactly as it
       always did on the estate-wide defaults. */
    const boot = async () => {
      const { list, canGlobal } = await loadOverview();
      const initial = !canGlobal && list.length === 1 ? list[0].storeId : null;
      setStoreId(initial);
      await loadSettings(initial);
      setLoading(false);
    };
    boot();
    fetchCategories();
  }, [loadSettings, loadOverview]);

  const selectScope = async (id) => {
    if (String(id || "") === String(storeId || "")) return;
    setStoreId(id);
    setFormError("");
    setLoading(true);
    await loadSettings(id);
    setLoading(false);
  };

  /* clicking Override only unlocks the control — the value is not the store's
     until it is saved, so nothing is written here */
  const startOverride = (fields) => {
    setOverridden((prev) => prev.concat(fields.filter((f) => !prev.includes(f))));
  };

  const revertFields = async (fields) => {
    if (!storeId) return;
    /* Only the two nullable branch columns can actually be cleared. Every
       other overridable field answers 409 revert_unavailable because its
       column is NOT NULL, so the DELETE is not fired for them — the pin is
       dropped on screen and the stored value stays until the next save
       overwrites it, which is what the toast below says. */
    const stored = fields.filter((f) => serverOverridden.includes(f) && REVERTIBLE.includes(f));
    const kept = fields.filter((f) => serverOverridden.includes(f) && !REVERTIBLE.includes(f));
    if (stored.length) {
      setReverting(fields[0]);
      const results = await Promise.all(
        stored.map((f) =>
          api.delete(`/admin/store-settings/stores/${storeId}/${encodeURIComponent(f)}`)
            .then(() => null)
            .catch((err) => err)
        )
      );
      setReverting("");
      /* a field this store never overrode is a 200 with cleared:false, not a
         404 — so anything that comes back as an error really did fail */
      if (results.every((r) => r !== null)) {
        const err = results[0];
        toast.error(err?.response?.data?.message || "Failed to revert this setting");
        return;
      }
    }
    if (globalRef.current) {
      applySettings(globalRef.current, fields);
    } else {
      /* no snapshot of the global row to fall back on — re-resolve rather than
         leave the field showing the value it no longer holds */
      await loadSettings(storeId);
    }
    setOverridden((prev) => prev.filter((f) => !fields.includes(f)));
    setServerOverridden((prev) => prev.filter((f) => !fields.includes(f)));
    toast.success(
      stored.length
        ? "Reverted to the global default"
        : kept.length
          ? "Override cleared here — this store keeps its saved value until you save a new one, because the column cannot hold \"not set\""
          : "Override discarded"
    );
    loadOverview();
  };

  /* props for one overridable field group — the first name is the group key */
  const ovr = (...fields) => ({
    storeMode,
    on: fields.some((f) => overridden.includes(f)),
    busy: reverting === fields[0],
    onOverride: () => startOverride(fields),
    onRevert: () => revertFields(fields),
  });

  /* WHO this page's edits reach. The tax mode is the one setting where that
     distinction changes the decision, so it is spelled out rather than left
     to the pill at the top of the page: a superadmin pinning one outlet must
     not be told the whole estate reprices (they may cancel a correct edit),
     and an estate-wide switch must not read as if it covered only the store
     whose name happens to be on screen. */
  const taxScope = () => {
    if (storeMode) return `${activeStore?.name || "this store"} only`;
    if (stores.length > 1) return "every store that has not pinned its own tax mode";
    return "every listed price";
  };

  /* Switching to "exclusive" is the one edit on this page that reprices a
     catalogue, and it does so without any price being touched — so it asks
     first. Switching BACK to inclusive needs no confirmation: it can only
     ever lower what a customer is charged. */
  const chooseTaxMode = (next) => {
    if (next === taxMode) return;
    if (next === "exclusive") {
      const who = storeMode
        ? `customers ordering from ${activeStore?.name || "this store"}`
        : "customers";
      const ok = window.confirm(
        `Add GST on top of the listed price for ${taxScope()}?\n\n` +
        `This means ${who} will pay MORE than the price shown on the product. ` +
        "No price is edited — the tax is added to every new bill instead.\n\n" +
        (storeMode
          ? "Other stores are NOT affected: they keep following the estate-wide default.\n\n"
          : "") +
        "Orders already placed are NOT affected: each one keeps the tax it was billed under.\n\n" +
        "This takes effect when you save."
      );
      if (!ok) return;
    }
    setTaxMode(next);
  };

  const handleSave = async () => {
    if (loadFailed) {
      setFormError("These settings could not be loaded — reload the page before saving.");
      return;
    }

    /* in store mode only the pinned fields travel, so only they are checked —
       an inherited value is the global row's problem, not this store's */
    const need = (f) => (storeMode ? overridden.includes(f) : true);

    if (need("storeName") && !storeName.trim()) { setFormError("Store name is required"); return; }
    if (need("openTime")  && !openTime)  { setFormError("Open time is required"); return; }
    if (need("closeTime") && !closeTime) { setFormError("Close time is required"); return; }
    if (need("millingFeePerKgFlour") && (millingFeeFlour === "" || isNaN(millingFeeFlour) || Number(millingFeeFlour) < 0)) { setFormError("Valid flour milling fee (₹/kg) is required"); return; }
    if (need("millingFeePerKgMasala") && (millingFeeMasala === "" || isNaN(millingFeeMasala) || Number(millingFeeMasala) < 0)) { setFormError("Valid masala milling fee (₹/kg) is required"); return; }
    if (need("roastChargePerKg") && (roastChargePerKg === "" || isNaN(roastChargePerKg) || Number(roastChargePerKg) < 0)) { setFormError("Valid roast charge (₹/kg) is required"); return; }
    if (need("roastCapacityFactor") && (roastCapacityFactor === "" || isNaN(roastCapacityFactor) || Number(roastCapacityFactor) < 1)) { setFormError("Roast time factor must be a number of at least 1"); return; }
    const timeRe = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (need("onlineOrderStartTime") && !timeRe.test(onlineOrderStartTime)) { setFormError("Valid online ordering start time (HH:mm) is required"); return; }
    if (need("onlineOrderEndTime")   && !timeRe.test(onlineOrderEndTime))   { setFormError("Valid online ordering end time (HH:mm) is required"); return; }
    if (need("minOrderValue") && (minOrderValue === "" || isNaN(minOrderValue) || Number(minOrderValue) < 0)) { setFormError("Valid minimum order value (₹) is required"); return; }
    if (need("freeDeliveryAbove") && (freeDeliveryAbove === "" || isNaN(freeDeliveryAbove) || Number(freeDeliveryAbove) < 0)) { setFormError("Valid free-delivery threshold (₹) is required"); return; }
    if (need("deliveryCharge") && (deliveryCharge === "" || isNaN(deliveryCharge) || Number(deliveryCharge) < 0)) { setFormError("Valid delivery charge (₹) is required"); return; }

    const pinned = OVERRIDABLE.filter((f) => overridden.includes(f));
    if (storeMode && pinned.length === 0) {
      setFormError("Nothing to save — override at least one field for this store first.");
      return;
    }

    setSaving(true); setFormError("");

    /* ONLY keys shop.CfgStoreSettings has a column for. The body is walked key
       by key and the FIRST one with no column aborts the WHOLE write with 400
       not_portable, before anything is stored — so a leftover gstin, camera
       list or reelo id here would fail the entire save rather than be dropped.
       Money goes out in RUPEES under the legacy names; the server multiplies
       by 100 and rounds. Sending both spellings of one setting is a 400, so
       each amount travels once. */
    const data = {
      storeName: storeName.trim(),
      taxMode,
      addressLine: addressLine.trim(),
      openTime,
      closeTime,
      pickupEnabled,
      deliveryEnabled,
      millingFeePerKgFlour: parseFloat(millingFeeFlour),
      millingFeePerKgMasala: parseFloat(millingFeeMasala),
      millingFeePerKg: parseFloat(millingFeeFlour), // legacy shared fee
      roastChargePerKg: parseFloat(roastChargePerKg),
      roastCapacityFactor: parseFloat(roastCapacityFactor),
      onlineOrderStartTime,
      onlineOrderEndTime,
      minOrderValue: parseFloat(minOrderValue),
      freeDeliveryAbove: parseFloat(freeDeliveryAbove),
      deliveryCharge: parseFloat(deliveryCharge),
      codForMillingEnabled,
      codEnabled,
      showGstOnBill,

      /* Migration 0036 gave these columns. They were rendered as disabled
         boxes and left out of the body, because sending one aborted the WHOLE
         save with 400 not_portable — the server refuses a key it cannot store
         rather than dropping it silently, which is why they had to be omitted
         until the columns existed.

         The grievance contact is estate-wide by design (one firm, one privacy
         notice), so it is sent only when editing the estate defaults; a
         per-store patch that included it would be refused. */
      gstin: gstin.trim(),
      fssaiNo: fssaiNo.trim(),
      upiVpa: upiVpa.trim(),
      receiptFooter: receiptFooter.trim(),
      supportPhone: supportPhone.trim(),
      supportWhatsapp: supportWhatsapp.trim(),
      supportEmail: supportEmail.trim(),
      minWeightGrams: parseInt(minWeightGrams, 10),
      weightStepGrams: parseInt(weightStepGrams, 10),
      liveStreamEnabled,
      liveStreamUrl: liveStreamUrl.trim(),
      ...(storeMode ? {} : {
        milledHsn: milledHsn.trim(),
        deliveryHsn: deliveryHsn.trim(),
        grievanceOfficerName: grievanceName.trim(),
        grievanceEmail: grievanceEmail.trim(),
        grievancePhone: grievancePhone.trim(),
      }),
    };

    try {
      if (storeMode) {
        /* only the pinned fields travel — everything else must stay inherited,
           so the whole form is never posted against a store */
        const patch = {};
        pinned.forEach((f) => { if (data[f] !== undefined) patch[f] = data[f]; });
        const res = await api.put(`/admin/store-settings/stores/${storeId}`, patch);
        setServerOverridden((prev) => prev.concat(Object.keys(patch).filter((f) => !prev.includes(f))));
        toast.success(`Overrides saved for ${activeStore?.name || "this store"}`);
        /* the reply is the resolved settings payload itself, _meta and all.
           A store with no settings row of its own gets one SEEDED from the
           estate defaults, which freezes them — that is a real change to how
           this store behaves, so it is said out loud rather than swallowed. */
        const meta = res?.data?._meta || {};
        if (meta.settingsRowSeededFromEstate) {
          toast.info(
            meta.note ||
            "This store had no settings row, so one was seeded from the estate defaults — later estate changes will no longer reach it."
          );
        }
        loadOverview();
      } else {
        const res = await api.put("/admin/store-settings", data);
        toast.success("Store settings updated successfully!");
        /* the saved, server-normalised row IS the body — no envelope. Prefer
           it over the payload we sent so the ₹ boxes show what was actually
           stored (paise, rounded) rather than what was typed. */
        const saved = res?.data;
        globalRef.current = saved || { ...(globalRef.current || {}), ...data };
        if (saved) applySettings(saved);

        /* The three LISTS go to their own endpoints. They were arrays on a
           Mongo document and are tables now, so they cannot ride on the scalar
           PUT — that walks the body key by key and refuses anything without a
           column, which is why these were disabled boxes until now.

           After the scalars, and each failure reported on its own: a camera
           list the server rejects must not make it look as though the fees did
           not save either. */
        await Promise.all([
          saveList("/admin/settings/cameras", { cameras }, "Cameras"),
          saveList("/admin/settings/delivery-windows", { windows: deliveryWindows }, "Delivery windows"),
          saveList("/admin/settings/featured-categories", { categoryIds: featuredIds }, "Home categories"),
        ]);
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Something went wrong";
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  /* One list, to its own endpoint. Reported separately so a rejected camera
     list does not read as "nothing saved" when the fees went through. */
  const saveList = async (url, body, label) => {
    try {
      await api.put(url, body);
    } catch (err) {
      const msg = err.response?.data?.message || "could not be saved";
      toast.error(`${label}: ${msg}`);
    }
  };

  /* SHOW THE PICKER WHENEVER THERE IS A SCOPE TO NAME — which is not the
     same as "more than one store exists". A store-bound admin gets exactly
     one row from stores-overview, so `stores.length > 1` hid the picker from
     precisely the people who cannot edit the estate row: the scope stayed
     null and Save answered 403 telling them to use their store's settings,
     with nothing on the page to select it. They now see one pill, already
     selected, which is what tells them what they are saving to. */
  const showScope = stores.length > 1 || (!canEditGlobal && stores.length > 0);

  /* an account holding the settings permission but bound to no store has
     nothing on this page it may write — say so, rather than letting Save
     discover it as a 403 */
  const noEditableScope = !canEditGlobal && !storeMode;

  return (
    <div>
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 style={{ margin: 0 }}>Store Settings</h5>
          </div>

          <div className="card-body">
            {/* ── Scope selector — hidden only when there is one scope and
                   the admin is allowed to edit it (the classic single-store
                   superadmin), so the page reads as it always has there ── */}
            {showScope && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                  {/* offered only to an admin who may actually write that row —
                      PUT / refuses everyone else, so a pill that always 403s
                      would be a control that exists to fail */}
                  {canEditGlobal && (
                    <button type="button" onClick={() => selectScope(null)} style={SCOPE_PILL(!storeMode)}>
                      All stores (default)
                    </button>
                  )}
                  {stores.map((s) => {
                    const active = String(s.storeId) === String(storeId);
                    return (
                      <button key={s.storeId} type="button" onClick={() => selectScope(s.storeId)} style={SCOPE_PILL(active)}>
                        {s.name}
                        {s.overriddenCount > 0 && (
                          <span style={COUNT_BADGE(active)}>{s.overriddenCount}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)" }}>
                  {storeMode ? (
                    <>
                      Editing <strong style={{ color: "var(--mly-text-soft)" }}>{activeStore?.name || "this store"}</strong>.
                      Every value follows the global default until you override it — the badge counts what this store has pinned.
                      Hours, address and delivery charges belong to the store record itself; edit those on{" "}
                      <Link to="/admin/stores" style={{ color: "var(--mly-heading)", fontWeight: 700 }}>Stores</Link>.
                    </>
                  ) : (
                    <>These are the estate-wide defaults — every store uses them unless it overrides the value.</>
                  )}
                  {!canEditGlobal && (
                    <>
                      {" "}Your account manages one store, so every change you save here is that store's own —
                      the estate-wide defaults are edited by a superadmin.
                    </>
                  )}
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : (
              <div style={{ maxWidth: 760 }}>
                {formError && (
                  <div style={{ background: "#fef2f2", color: "#dc2626", padding: "10px 14px", borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
                    {formError}
                  </div>
                )}

                {/* ── Store Details ── */}
                <div style={SECTION_TITLE}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  Store Details
                </div>

                <div className="row" style={{ marginBottom: 16 }}>
                  <div className="col-md-6">
                    <StoreOwned storeMode={storeMode}>
                      <label style={LABEL}>Store Name <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="text" value={storeName} onChange={(e) => setStoreName(e.target.value)}
                        placeholder="e.g. Milliyo Fresh Mill" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </StoreOwned>
                  </div>
                  <div className="col-md-6">
                    {/* TODO(cutover): server does not return timezone, and
                        refuses it on both settings PUTs — day boundaries
                        resolve against Asia/Kolkata everywhere now. */}
                    <Ovr {...gone("Day boundaries resolve against Asia/Kolkata everywhere.")}>
                      <label style={LABEL}>Timezone</label>
                      <select value={timezone} onChange={(e) => setTimezone(e.target.value)} style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}>
                        <option value="Asia/Kolkata">India — IST (Asia/Kolkata)</option>
                        <option value="Asia/Dubai">Gulf — GST (Asia/Dubai)</option>
                        <option value="Asia/Colombo">Sri Lanka (Asia/Colombo)</option>
                        <option value="Asia/Kathmandu">Nepal (Asia/Kathmandu)</option>
                        <option value="Asia/Singapore">Singapore (Asia/Singapore)</option>
                        <option value="Europe/London">UK (Europe/London)</option>
                        <option value="America/New_York">US Eastern (America/New_York)</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </Ovr>
                  </div>
                  <div className="col-md-6">
                    <StoreOwned storeMode={storeMode}>
                      <label style={LABEL}>Address <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional)</span></label>
                      <input type="text" value={addressLine} onChange={(e) => setAddressLine(e.target.value)}
                        placeholder="e.g. 12, Main Road, Coimbatore" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </StoreOwned>
                  </div>
                  <div className="col-md-6">
                    <Ovr>
                      <label style={LABEL}>GSTIN <span style={{ color: "#ffc500", fontWeight: 400 }}>(for bills &amp; GST report)</span></label>
                      <input type="text" value={gstin} maxLength={15}
                        onChange={(e) => setGstin(e.target.value.toUpperCase())}
                        placeholder="15-character GSTIN, e.g. 33ABCDE1234F1Z5" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                      <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                        Printed on bills and used as the filing identity in the GST report.
                      </div>
                    </Ovr>
                  </div>
                </div>

                {/* ── HSN for lines with no product behind them ──
                    A milled blend and the delivery charge are invoiced with no
                    product, so their codes live here. Estate-wide: one firm, one
                    classification. Blank leaves them without one, and the GST
                    report flags that. Which code applies is the CA's call. */}
                {!storeMode && (
                  <div className="row g-3" style={{ marginTop: 4 }}>
                    <div className="col-md-6">
                      <label style={LABEL}>HSN for milled items</label>
                      <input type="text" value={milledHsn} maxLength={8} inputMode="numeric"
                        onChange={(e) => setMilledHsn(e.target.value.replace(/\D/g, ""))}
                        placeholder="e.g. 1101 wheat flour, 1102 other cereal flours" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                      <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                        Printed on every custom-milled blend on the invoice. 4, 6 or 8 digits.
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label style={LABEL}>HSN / SAC for delivery</label>
                      <input type="text" value={deliveryHsn} maxLength={8} inputMode="numeric"
                        onChange={(e) => setDeliveryHsn(e.target.value.replace(/\D/g, ""))}
                        placeholder="Ask your CA, e.g. 996812" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                      <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                        Printed on the delivery line. Applies to invoices issued after you save.
                      </div>
                    </div>
                  </div>
                )}

                {/* ── What the printed bill carries ──
                    The layout of the bill is fixed — a GST tax invoice on 80mm
                    thermal paper, because Rule 46 decides most of what is on it
                    and none of it is decoration. What is editable is whose
                    invoice it is: the name and address above, the GSTIN, and
                    these three. They are read at print time, so a correction
                    here shows on the very next bill without a restart. */}
                <div className="row g-3" style={{ marginTop: 4 }}>
                  <div className="col-12">
                    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--mly-text-muted)", marginBottom: 2 }}>
                      Printed bill
                    </div>
                  </div>
                  <div className="col-12">
                    <label style={LABEL}>Logo on the bill <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional)</span></label>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ flex: 1, fontSize: 12, color: "var(--mly-text-muted)", fontVariantNumeric: "tabular-nums" }}>
                        {billLogo
                          ? `Stored — ${billLogo.widthDots} x ${billLogo.heightDots} printer dots`
                          : "None. The bill prints the shop name only."}
                      </span>
                      <label style={{ ...BTN, opacity: billLogoBusy ? 0.5 : 1, cursor: billLogoBusy ? "not-allowed" : "pointer" }}>
                        {billLogoBusy ? "Working…" : billLogo ? "Replace" : "Upload a PNG"}
                        <input type="file" accept="image/png" style={{ display: "none" }} disabled={billLogoBusy}
                          onChange={(e) => { uploadBillLogo(e.target.files?.[0]); e.target.value = ""; }} />
                      </label>
                      {billLogo && (
                        <button type="button" style={BTN} disabled={billLogoBusy} onClick={removeBillLogo}>Remove</button>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                      A thermal printer prints dots, not colours, so the picture is reduced to black
                      and white when it is saved. A wide, simple logo about 600 pixels across reads
                      best. Where it sits on the bill is set in <Link to="/admin/billdesign" style={{ color: "var(--mly-heading)" }}>Bill design</Link>.
                    </div>
                  </div>

                  <div className="col-md-6">
                    <Ovr>
                      <label style={LABEL}>FSSAI licence <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional)</span></label>
                      <input type="text" value={fssaiNo} maxLength={14}
                        onChange={(e) => setFssaiNo(e.target.value.replace(/\D/g, ""))}
                        placeholder="14 digits, e.g. 11223344556677" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                      <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                        This outlet's own licence number. It prints under the GSTIN.
                      </div>
                    </Ovr>
                  </div>
                  <div className="col-md-6">
                    <Ovr>
                      <label style={LABEL}>UPI ID for the bill QR <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional)</span></label>
                      <input type="text" value={upiVpa}
                        onChange={(e) => setUpiVpa(e.target.value.trim())}
                        placeholder="e.g. milliyo@okhdfcbank" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                      <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                        A "scan to pay" QR for the exact bill amount is printed when this is set. Leave it empty and no QR is printed.
                      </div>
                    </Ovr>
                  </div>
                  <div className="col-12">
                    <Ovr>
                      <label style={LABEL}>Closing line on the bill <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional)</span></label>
                      <input type="text" value={receiptFooter} maxLength={120}
                        onChange={(e) => setReceiptFooter(e.target.value)}
                        placeholder="e.g. Thank you. Milled fresh, today." style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                      <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                        Replaces the default "Thank you. Visit again." The returns line below it is printed on every bill and is not editable here.
                      </div>
                    </Ovr>
                  </div>
                </div>

                {/* ── Pricing & GST ──
                    Deliberately two labelled options, not a dropdown: this is
                    the only control on the page that changes what customers
                    are charged, and a one-word dropdown would hide that. */}
                <div className="row" style={{ marginBottom: 28 }}>
                  <div className="col-12">
                    <Ovr {...ovr("taxMode")}>
                      <label style={LABEL}>How the listed price relates to GST</label>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {TAX_MODE_CHOICES.map((c) => {
                          const active = taxMode === c.value;
                          return (
                            <label
                              key={c.value}
                              style={{
                                flex: "1 1 300px", display: "flex", gap: 10, alignItems: "flex-start",
                                padding: "12px 14px", borderRadius: 12, cursor: "pointer", marginBottom: 0,
                                border: `1.5px solid ${active ? "var(--milliyo-forest)" : "var(--mly-border)"}`,
                                background: active ? "var(--mly-accent-soft)" : "var(--mly-surface)",
                              }}
                            >
                              <input
                                type="radio"
                                name="taxMode"
                                value={c.value}
                                checked={active}
                                onChange={() => chooseTaxMode(c.value)}
                                style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer", flex: "0 0 auto" }}
                              />
                              <span>
                                <span style={{
                                  display: "block", fontSize: 13, fontWeight: 700,
                                  color: active ? "var(--mly-heading)" : "var(--mly-text-soft)",
                                }}>
                                  {c.title}
                                </span>
                                <span style={{ display: "block", fontSize: 11.5, color: "var(--mly-text-soft)", marginTop: 3, lineHeight: 1.5 }}>
                                  {c.detail}
                                </span>
                                <span style={{ display: "block", fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4, lineHeight: 1.5 }}>
                                  {c.note}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>

                      {taxMode === "exclusive" && (
                        <div style={{
                          marginTop: 10, padding: "10px 14px", borderRadius: 10, fontSize: 12, lineHeight: 1.55,
                          background: "#fff8e6", color: "#8a5a00", border: "1px solid #f0d68a",
                        }}>
                          {/* named scope, always: an override pinned to one outlet
                              must not read as an estate-wide reprice, and an
                              estate-wide switch must not read as one store's */}
                          <strong>
                            {storeMode
                              ? `Customers ordering from ${activeStore?.name || "this store"} pay more than the listed price.`
                              : "Customers pay more than the listed price."}
                          </strong>{" "}
                          GST is added to the catalogue price at checkout from the next order onwards, for{" "}
                          {taxScope()}. Orders already placed keep the tax they were billed under — nothing is
                          recalculated.
                        </div>
                      )}

                      <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 8, lineHeight: 1.55 }}>
                        This setting decides whether GST comes out of the listed price or goes on top of it.
                        Whether the customer is SHOWN it is the switch below.
                      </div>
                    </Ovr>
                  </div>
                </div>

                {/* Display only. The tax is charged and filed the same either
                    way, and the tax invoice email -- a GST document -- has its
                    own switch under Email settings. */}
                <div className="row" style={{ marginBottom: 28 }}>
                  <div className="col-md-8">
                    <Ovr {...ovr("showGstOnBill")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--mly-border)" }}>
                        <label className="switch" style={{ marginBottom: 0 }}>
                          <input type="checkbox" checked={showGstOnBill} onChange={(e) => setShowGstOnBill(e.target.checked)} />
                          <span className="slider round" />
                        </label>
                        <span>
                          <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: showGstOnBill ? "var(--mly-heading)" : "var(--mly-text-muted)" }}>
                            Show GST on the customer's bill
                          </span>
                          <span style={{ display: "block", fontSize: 11.5, color: "var(--mly-text-soft)", marginTop: 2, lineHeight: 1.5 }}>
                            "Includes GST ₹X · CGST + SGST" under the total, in the app and in order emails.
                            Off hides it; the tax charged and filed does not change.
                          </span>
                        </span>
                      </div>
                    </Ovr>
                  </div>
                </div>

                <div className="row" style={{ marginBottom: 16 }}>
                  <div className="col-md-6">
                    <StoreOwned storeMode={storeMode}>
                      <label style={LABEL}>Open Time <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="time" step="900" value={openTime} onChange={(e) => setOpenTime(e.target.value)}
                        style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </StoreOwned>
                  </div>
                  <div className="col-md-6">
                    <StoreOwned storeMode={storeMode}>
                      <label style={LABEL}>Close Time <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="time" step="900" value={closeTime} onChange={(e) => setCloseTime(e.target.value)}
                        style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </StoreOwned>
                  </div>
                </div>

                <div className="row" style={{ marginBottom: 28 }}>
                  <div className="col-md-6">
                    <StoreOwned storeMode={storeMode}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--mly-border)" }}>
                        <label className="switch" style={{ marginBottom: 0 }}>
                          <input type="checkbox" checked={pickupEnabled} onChange={(e) => setPickupEnabled(e.target.checked)} />
                          <span className="slider round" />
                        </label>
                        <span style={{ fontSize: 13, fontWeight: 600, color: pickupEnabled ? "var(--mly-heading)" : "var(--mly-text-muted)" }}>
                          Store Pickup {pickupEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </StoreOwned>
                  </div>
                  <div className="col-md-6">
                    <StoreOwned storeMode={storeMode}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--mly-border)" }}>
                        <label className="switch" style={{ marginBottom: 0 }}>
                          <input type="checkbox" checked={deliveryEnabled} onChange={(e) => setDeliveryEnabled(e.target.checked)} />
                          <span className="slider round" />
                        </label>
                        <span style={{ fontSize: 13, fontWeight: 600, color: deliveryEnabled ? "var(--mly-heading)" : "var(--mly-text-muted)" }}>
                          Home Delivery {deliveryEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </StoreOwned>
                  </div>
                </div>

                {/* ── Support contact (used by the customer app) ── */}
                <div style={SECTION_TITLE}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.09 4.18 2 2 0 0 1 4.08 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  Support Contact
                </div>
                <div className="row" style={{ marginBottom: 8 }}>
                  <div className="col-md-4">
                    <Ovr>
                      <label style={LABEL}>Support phone</label>
                      <input value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)}
                        placeholder="10-digit number" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                    </Ovr>
                  </div>
                  <div className="col-md-4">
                    <Ovr>
                      <label style={LABEL}>WhatsApp number</label>
                      <input value={supportWhatsapp} onChange={(e) => setSupportWhatsapp(e.target.value)}
                        placeholder="same as phone if blank" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                    </Ovr>
                  </div>
                  <div className="col-md-4">
                    <Ovr>
                      <label style={LABEL}>Support email</label>
                      <input value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)}
                        placeholder="milliyo.foods@gmail.com" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                    </Ovr>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 28 }}>
                  The customer app reads these for its Call and WhatsApp actions. Leave blank to hide those buttons in the app entirely — nothing is hardcoded in the build.
                </div>

                {/* ── Grievance Officer (DPDPA) ── */}
                <div style={SECTION_TITLE}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                  Grievance Officer (DPDPA)
                </div>
                <div className="row" style={{ marginBottom: 8 }}>
                  <div className="col-md-4">
                    <Ovr>
                      <label style={LABEL}>Officer name</label>
                      <input value={grievanceName} onChange={(e) => setGrievanceName(e.target.value)}
                        placeholder="e.g. Priya Nair" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                    </Ovr>
                  </div>
                  <div className="col-md-4">
                    <Ovr>
                      <label style={LABEL}>Grievance email</label>
                      <input value={grievanceEmail} onChange={(e) => setGrievanceEmail(e.target.value)}
                        placeholder="grievance@milliyo.com" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                    </Ovr>
                  </div>
                  <div className="col-md-4">
                    <Ovr>
                      <label style={LABEL}>Grievance phone</label>
                      <input value={grievancePhone} onChange={(e) => setGrievancePhone(e.target.value)}
                        placeholder="10-digit number" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                    </Ovr>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 28 }}>
                  Shown in the app under Privacy &amp; Data Protection — DPDPA requires a reachable grievance contact. Leave blank to fall back to the Support Contact above.
                </div>

                {/* ── App Home Categories ── */}
                <div style={SECTION_TITLE}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                  App Home Categories
                </div>

                <Ovr>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                    {categories.map((c) => {
                      const idx = featuredIds.indexOf(c.id);
                      const selected = idx !== -1;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() =>
                            setFeaturedIds(selected
                              ? featuredIds.filter((id) => id !== c.id)
                              : [...featuredIds, c.id])
                          }
                          style={{
                            border: `1.5px solid ${selected ? "var(--milliyo-forest)" : "var(--mly-border)"}`,
                            background: selected ? "var(--milliyo-forest)" : "var(--mly-surface)",
                            color: selected ? "#E8C96A" : "var(--mly-text-soft)",
                            borderRadius: 999, padding: "6px 14px", fontSize: 13,
                            fontWeight: 600, cursor: "pointer",
                            display: "inline-flex", alignItems: "center", gap: 6,
                          }}
                        >
                          {selected && (
                            <span style={{
                              background: "#C9952A", color: "#fff", borderRadius: "50%",
                              width: 18, height: 18, fontSize: 11, lineHeight: "18px",
                              textAlign: "center", fontWeight: 700,
                            }}>{idx + 1}</span>
                          )}
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                </Ovr>
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 28 }}>
                  Selected categories appear as the circle shortcuts on the app's home screen, in the order you click them. Select none to show every category.
                </div>

                {/* ── Custom Milling Charges ── */}
                <div style={SECTION_TITLE}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                  Custom Milling Charges
                </div>

                <div className="row" style={{ marginBottom: 16 }}>
                  <div className="col-md-6">
                    {/* the legacy single-fee path shadows the flour fee, so the two
                        are pinned and reverted together */}
                    <Ovr {...ovr("millingFeePerKgFlour", "millingFeePerKg")}>
                      <label style={LABEL}>Flour Milling Fee (₹/kg) <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="number" value={millingFeeFlour} onChange={(e) => setMillingFeeFlour(e.target.value)}
                        placeholder="e.g. 10" min="0" step="0.5" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </Ovr>
                  </div>
                  <div className="col-md-6">
                    <Ovr {...ovr("millingFeePerKgMasala")}>
                      <label style={LABEL}>Masala Milling Fee (₹/kg) <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="number" value={millingFeeMasala} onChange={(e) => setMillingFeeMasala(e.target.value)}
                        placeholder="e.g. 15" min="0" step="0.5" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </Ovr>
                  </div>
                </div>

                <div className="row" style={{ marginBottom: 16 }}>
                  <div className="col-md-6">
                    <Ovr {...ovr("roastChargePerKg")}>
                      <label style={LABEL}>Roast Charge (₹/kg) <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="number" value={roastChargePerKg} onChange={(e) => setRoastChargePerKg(e.target.value)}
                        placeholder="e.g. 5" min="0" step="0.5" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </Ovr>
                  </div>
                  <div className="col-md-6">
                    <Ovr {...ovr("roastCapacityFactor")}>
                      <label style={LABEL}>Roast Time Factor <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="number" value={roastCapacityFactor} onChange={(e) => setRoastCapacityFactor(e.target.value)}
                        placeholder="e.g. 1.5" min="1" step="0.1" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                      <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                        Roasting + milling holds the mill longer — a roasted order books weight × this factor of the slot's capacity.
                      </div>
                    </Ovr>
                  </div>
                </div>

                <div className="row" style={{ marginBottom: 16 }}>
                  <div className="col-md-6">
                    <Ovr>
                      <label style={LABEL}>Minimum Weight (g) <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="number" value={minWeightGrams} onChange={(e) => setMinWeightGrams(e.target.value)}
                        placeholder="e.g. 500" min="0" step="50" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </Ovr>
                  </div>
                  <div className="col-md-6">
                    <Ovr>
                      <label style={LABEL}>Weight Step (g) <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="number" value={weightStepGrams} onChange={(e) => setWeightStepGrams(e.target.value)}
                        placeholder="e.g. 250" min="0" step="50" style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </Ovr>
                  </div>
                </div>

                {/* ── Online Ordering Window ── */}
                <div style={{ ...SECTION_TITLE, marginTop: 12 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  Online Ordering Window
                </div>

                <div className="row" style={{ marginBottom: 8 }}>
                  <div className="col-md-6">
                    <StoreOwned storeMode={storeMode}>
                      <label style={LABEL}>Order Start Time <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="time" step="900" value={onlineOrderStartTime} onChange={(e) => setOnlineOrderStartTime(e.target.value)}
                        style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </StoreOwned>
                  </div>
                  <div className="col-md-6">
                    <StoreOwned storeMode={storeMode}>
                      <label style={LABEL}>Order End Time <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="time" step="900" value={onlineOrderEndTime} onChange={(e) => setOnlineOrderEndTime(e.target.value)}
                        style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                    </StoreOwned>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 28 }}>
                  Orders are accepted only inside this window — can be narrower than store hours.
                </div>

                {/* ── Delivery & Order Rules ── */}
                <div style={SECTION_TITLE}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="3" width="15" height="13" rx="1" />
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                    <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                  Delivery &amp; Order Rules
                </div>

                <div className="row" style={{ marginBottom: 8 }}>
                  <div className="col-md-4">
                    <StoreOwned storeMode={storeMode}>
                      <label style={LABEL}>Minimum Order (₹) <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="number" min="0" value={minOrderValue} onChange={(e) => setMinOrderValue(e.target.value)}
                        style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                    </StoreOwned>
                  </div>
                  <div className="col-md-4">
                    <StoreOwned storeMode={storeMode}>
                      <label style={LABEL}>Free Delivery Above (₹) <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="number" min="0" value={freeDeliveryAbove} onChange={(e) => setFreeDeliveryAbove(e.target.value)}
                        style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                    </StoreOwned>
                  </div>
                  <div className="col-md-4">
                    <StoreOwned storeMode={storeMode}>
                      <label style={LABEL}>Delivery Charge (₹) <span style={{ color: "#ef4444" }}>*</span></label>
                      <input type="number" min="0" value={deliveryCharge} onChange={(e) => setDeliveryCharge(e.target.value)}
                        style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                    </StoreOwned>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 16 }}>
                  Home-delivery orders below the minimum are rejected; orders under the free-delivery
                  threshold pay the delivery charge.
                </div>

                <Ovr>
                  <label style={{ ...LABEL, display: "block", marginBottom: 8 }}>
                    Prepacked Delivery Promise
                    <span style={{ fontWeight: 400, color: "var(--mly-text-muted)", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                      order placed between the times → delivered by. Live-milling orders follow their slot instead.
                    </span>
                  </label>
                  {deliveryWindows.map((w, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 8, flexWrap: "wrap" }}>
                      <div>
                        <label style={{ ...LABEL, fontSize: 9.5 }}>Order From</label>
                        <input type="time" step="900" value={w.orderFrom}
                          onChange={(e) => setDeliveryWindows((p) => p.map((x, j) => j === i ? { ...x, orderFrom: e.target.value } : x))}
                          style={{ ...INPUT, width: 130 }} />
                      </div>
                      <div>
                        <label style={{ ...LABEL, fontSize: 9.5 }}>Order To</label>
                        <input type="time" step="900" value={w.orderTo}
                          onChange={(e) => setDeliveryWindows((p) => p.map((x, j) => j === i ? { ...x, orderTo: e.target.value } : x))}
                          style={{ ...INPUT, width: 130 }} />
                      </div>
                      <div>
                        <label style={{ ...LABEL, fontSize: 9.5 }}>Deliver By</label>
                        <input type="time" step="900" value={w.deliverBy}
                          onChange={(e) => setDeliveryWindows((p) => p.map((x, j) => j === i ? { ...x, deliverBy: e.target.value } : x))}
                          style={{ ...INPUT, width: 130 }} />
                      </div>
                      <button type="button" onClick={() => setDeliveryWindows((p) => p.filter((_, j) => j !== i))}
                        style={{ width: 38, height: 42, borderRadius: 10, border: "none", background: "#fff5f5", color: "#ef4444", cursor: "pointer", fontWeight: 800 }}>
                        ×
                      </button>
                    </div>
                  ))}
                  <button type="button"
                    onClick={() => setDeliveryWindows((p) => [...p, { orderFrom: "", orderTo: "", deliverBy: "" }])}
                    style={{ padding: "8px 16px", borderRadius: 8, border: "1.5px dashed var(--mly-heading)", background: "var(--mly-accent-soft)", color: "var(--mly-heading)", fontWeight: 700, fontSize: 13, cursor: "pointer", marginBottom: 28 }}>
                    + Add window
                  </button>
                </Ovr>

                {/* ── Live Mill Stream ── */}
                <div style={SECTION_TITLE}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                  Live Mill Stream
                </div>

                {/* the switch is meaningless without its URL, so the pair is one override */}
                <Ovr>
                  <div className="row" style={{ marginBottom: 8 }}>
                    <div className="col-md-6">
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--mly-border)" }}>
                        <label className="switch" style={{ marginBottom: 0 }}>
                          <input type="checkbox" checked={liveStreamEnabled} onChange={(e) => setLiveStreamEnabled(e.target.checked)} />
                          <span className="slider round" />
                        </label>
                        <span style={{ fontSize: 13, fontWeight: 600, color: liveStreamEnabled ? "var(--mly-heading)" : "var(--mly-text-muted)" }}>
                          Live Stream {liveStreamEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label style={LABEL}>
                        Stream URL {liveStreamEnabled
                          ? <span style={{ color: "#ef4444" }}>*</span>
                          : <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional)</span>}
                      </label>
                      <input type="text" value={liveStreamUrl} onChange={(e) => setLiveStreamUrl(e.target.value)}
                        placeholder={streamHlsBase ? joinHlsUrl(streamHlsBase, "mill") : "https://.../mill.m3u8 (HLS)"} style={INPUT}
                        onFocus={(e) => e.target.style.borderColor = "var(--mly-heading)"}
                        onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                      />
                      {streamHlsBase && !liveStreamUrl.trim() && (
                        <button type="button"
                          onClick={() => setLiveStreamUrl(joinHlsUrl(streamHlsBase, "mill"))}
                          style={{ border: "none", background: "none", padding: 0, marginTop: 4, cursor: "pointer", fontSize: 10.5, fontWeight: 700, color: "var(--mly-heading)", textDecoration: "underline" }}>
                          Use this shop's stream URL
                        </button>
                      )}
                    </div>
                  </div>
                </Ovr>
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 28 }}>
                  When enabled, customers with live-milling orders can watch the mill.
                  {streamHlsBase
                    ? <> This shop streams from <code>{streamHlsBase}</code>.</>
                    : <> <strong>No stream host is configured</strong>, so the switch above has nothing to play.</>}
                  {liveStreamEnabled && !liveStreamUrl.trim() && (
                    <span style={{ color: "#9a6b00", fontWeight: 700 }}>
                      {" "}Live Stream is on but no URL is set — customers will see the option and nothing behind it.
                    </span>
                  )}
                </div>

                {/* ── Live Milling Cameras ── */}
                <div style={SECTION_TITLE}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  Live Milling Cameras
                </div>

                <Ovr>
                  {cameras.map((cam, i) => (
                    <div key={i} style={{
                      display: "flex", gap: 10, alignItems: "flex-end",
                      marginBottom: 10, flexWrap: "wrap",
                      padding: "12px 12px", borderRadius: 12,
                      border: "1.5px solid var(--mly-border)", background: "var(--mly-surface-2)",
                    }}>
                      <div style={{ flex: "1 1 150px", minWidth: 150 }}>
                        <label style={{ ...LABEL, fontSize: 9.5 }}>Camera Name</label>
                        <input type="text" value={cam.name}
                          placeholder="e.g. Flour Mill 1"
                          onChange={(e) => setCameras((p) => p.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                          style={{ ...INPUT, background: "var(--mly-surface)" }} />
                      </div>
                      <div style={{ flex: "2 1 220px", minWidth: 220 }}>
                        <label style={{ ...LABEL, fontSize: 9.5 }}>Stream URL</label>
                        <input type="text" value={cam.url}
                          placeholder={streamHlsBase ? joinHlsUrl(streamHlsBase, streamSlug(cam.name, i)) : "https://…/cam.m3u8"}
                          onChange={(e) => setCameras((p) => p.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                          style={{ ...INPUT, background: "var(--mly-surface)" }} />
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                          {/* Suggest, rather than overwrite: a camera already
                              pointing somewhere deliberate must not be rewritten
                              because its name changed. */}
                          {streamHlsBase && cam.url !== joinHlsUrl(streamHlsBase, streamSlug(cam.name, i)) && (
                            <button type="button"
                              onClick={() => setCameras((p) => p.map((x, j) => j === i
                                ? { ...x, url: joinHlsUrl(streamHlsBase, streamSlug(x.name, j)) } : x))}
                              style={{ border: "none", background: "none", padding: 0, cursor: "pointer", fontSize: 10.5, fontWeight: 700, color: "var(--mly-heading)", textDecoration: "underline" }}>
                              Use this shop's stream URL
                            </button>
                          )}
                          <button type="button"
                            disabled={!cam.url || camProbe[i] === "checking"}
                            onClick={async () => {
                              setCamProbe((p) => ({ ...p, [i]: "checking" }));
                              /* no-cors: the stream host is a different origin
                                 and will not send CORS headers, so the response
                                 is opaque and only reachability can be read off
                                 it. Enough to tell a typo from a live camera,
                                 which is the question being asked. */
                              try {
                                await fetch(cam.url, { method: "GET", mode: "no-cors", cache: "no-store" });
                                setCamProbe((p) => ({ ...p, [i]: "ok" }));
                              } catch {
                                setCamProbe((p) => ({ ...p, [i]: "fail" }));
                              }
                            }}
                            style={{ border: "none", background: "none", padding: 0, cursor: cam.url ? "pointer" : "default", fontSize: 10.5, fontWeight: 700, color: cam.url ? "var(--mly-heading)" : "var(--mly-text-muted)", textDecoration: "underline" }}>
                            {camProbe[i] === "checking" ? "Testing…" : "Test"}
                          </button>
                          {camProbe[i] === "ok" && (
                            <span style={{ fontSize: 10.5, fontWeight: 700, color: "#16a34a" }}>reachable</span>
                          )}
                          {camProbe[i] === "fail" && (
                            <span style={{ fontSize: 10.5, fontWeight: 700, color: "#ef4444" }}>no answer — check the URL</span>
                          )}
                        </div>
                      </div>
                      <div style={{ flex: "0 0 130px" }}>
                        <label style={{ ...LABEL, fontSize: 9.5 }}>Mill Type</label>
                        <select value={cam.millType}
                          onChange={(e) => setCameras((p) => p.map((x, j) => j === i ? { ...x, millType: e.target.value } : x))}
                          style={{ ...INPUT, background: "var(--mly-surface)", cursor: "pointer" }}>
                          <option value="flour">Flour</option>
                          <option value="masala">Masala</option>
                          <option value="general">General</option>
                        </select>
                      </div>
                      <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, paddingBottom: 12 }}>
                        <input
                          type="checkbox"
                          id={`cam-show-${i}`}
                          checked={cam.showToCustomer}
                          onChange={(e) => setCameras((p) => p.map((x, j) => j === i ? { ...x, showToCustomer: e.target.checked } : x))}
                          style={{ width: 16, height: 16, cursor: "pointer" }}
                        />
                        <label htmlFor={`cam-show-${i}`} style={{ fontSize: 12, fontWeight: 600, color: "var(--mly-text-soft)", marginBottom: 0, cursor: "pointer" }}>
                          Show to customer
                        </label>
                      </div>
                      <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, paddingBottom: 12 }}>
                        <input
                          type="checkbox"
                          id={`cam-enabled-${i}`}
                          checked={cam.enabled}
                          onChange={(e) => setCameras((p) => p.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x))}
                          style={{ width: 16, height: 16, cursor: "pointer" }}
                        />
                        <label htmlFor={`cam-enabled-${i}`} style={{ fontSize: 12, fontWeight: 600, color: "var(--mly-text-soft)", marginBottom: 0, cursor: "pointer" }}>
                          Enabled
                        </label>
                      </div>
                      <button type="button" onClick={() => setCameras((p) => p.filter((_, j) => j !== i))}
                        title="Remove camera"
                        style={{ width: 38, height: 42, borderRadius: 10, border: "none", background: "#fff5f5", color: "#ef4444", cursor: "pointer", fontWeight: 800 }}>
                        ×
                      </button>
                    </div>
                  ))}
                  <button type="button"
                    onClick={() => setCameras((p) => [...p, {
                      name: "",
                      /* Prefilled, not blank. The server already knows the base
                         it serves HLS from; asking the shop to retype it is how
                         a typo becomes a camera that plays nowhere. Editable --
                         a camera on YouTube or an NVR has its own URL. */
                      url: streamHlsBase ? joinHlsUrl(streamHlsBase, streamSlug("", p.length)) : "",
                      millType: "general",
                      showToCustomer: true,
                      enabled: true,
                    }])}
                    style={{ padding: "8px 16px", borderRadius: 8, border: "1.5px dashed var(--mly-heading)", background: "var(--mly-accent-soft)", color: "var(--mly-heading)", fontWeight: 700, fontSize: 13, cursor: "pointer", marginBottom: 8 }}>
                    + Add camera
                  </button>
                </Ovr>
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 28 }}>
                  Cameras marked "Show to customer" appear in the mobile app while an order is being milled.
                  All enabled cameras appear on the admin Live View page.
                </div>

                {/* ── Payments ── */}
                <div style={SECTION_TITLE}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                  Payments
                </div>

                {/* Cash on delivery for this store at all; the live-milling switch
                    below narrows it further for baskets with milling in them.
                    Checkout enforces both, and the app hides cash with the reason. */}
                <div className="row" style={{ marginBottom: 8 }}>
                  <div className="col-md-8">
                    <Ovr {...ovr("codEnabled")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--mly-border)" }}>
                        <label className="switch" style={{ marginBottom: 0 }}>
                          <input type="checkbox" checked={codEnabled} onChange={(e) => setCodEnabled(e.target.checked)} />
                          <span className="slider round" />
                        </label>
                        <span style={{ fontSize: 13, fontWeight: 600, color: codEnabled ? "var(--mly-heading)" : "var(--mly-text-muted)" }}>
                          Allow cash on delivery
                        </span>
                      </div>
                    </Ovr>
                  </div>
                </div>

                <div className="row" style={{ marginBottom: 8 }}>
                  <div className="col-md-8">
                    <Ovr {...ovr("codForMillingEnabled")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--mly-border)" }}>
                        <label className="switch" style={{ marginBottom: 0 }}>
                          <input type="checkbox" checked={codForMillingEnabled} onChange={(e) => setCodForMillingEnabled(e.target.checked)} />
                          <span className="slider round" />
                        </label>
                        <span style={{ fontSize: 13, fontWeight: 600, color: codForMillingEnabled ? "var(--mly-heading)" : "var(--mly-text-muted)" }}>
                          Allow COD for Live-Milling orders
                        </span>
                      </div>
                    </Ovr>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 28 }}>
                  When disabled, bills that contain a custom-milled item must be paid online —
                  Cash on Delivery is hidden at checkout for those orders.
                </div>

                {/* ── Loyalty (Reelo) — moved to its own console ── */}
                <div style={SECTION_TITLE}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  Loyalty (Reelo)
                </div>

                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, flexWrap: "wrap", marginBottom: storeMode ? 8 : 28,
                  background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)",
                  borderRadius: 12, padding: "12px 16px",
                }}>
                  <span style={{ fontSize: 13, color: "var(--mly-text-soft)" }}>
                    Reelo keys, Yo Points rules, the test bench and the call audit log now live on the
                    Loyalty &amp; Reelo page.
                  </span>
                  <Link to="/admin/loyalty" style={{
                    padding: "8px 18px", borderRadius: 10, background: "var(--mly-heading)",
                    color: "var(--mly-on-accent)", fontSize: 13, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap",
                  }}>
                    Open Loyalty &amp; Reelo
                  </Link>
                </div>
                {/* TODO(cutover): no server endpoint STORES the outlet
                    credentials. reeloVendorId / reeloMerchantId /
                    reeloCustomerKey are connection-level on the ledger service
                    and refused by name by both settings PUTs; the only endpoint
                    that accepts them, PUT /admin/loyalty/credentials, validates
                    and then answers 501 settings_not_storable because
                    shop.CfgStoreSettings has no reelo_* columns. The editor
                    that used to sit here was removed rather than left posting
                    into a refusal — and the note below says where the values
                    actually come from instead of naming a page that cannot save
                    them either. */}
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 28, lineHeight: 1.55 }}>
                  The outlet credentials — Vendor ID, Merchant ID and Customer Key — can no longer be edited
                  from the portal at all: the ledger service has no column for them, so every save is refused.
                  They are set through the server's <code>REELO_*</code> environment variables, and the{" "}
                  <Link to="/admin/loyalty" style={{ color: "var(--mly-heading)", fontWeight: 700 }}>Loyalty &amp; Reelo</Link>{" "}
                  page shows the resolved values and tests the connection. They are one estate-wide set: a
                  per-store vendor id against an estate-wide key would authenticate as the estate but address
                  another outlet.
                </div>

                {/* an admin who may not edit the estate row and has no store
                    bound to their account has no scope to save into. Say it
                    here rather than letting Save find out as a 403. */}
                {noEditableScope && (
                  <div style={{
                    background: "#fff8e6", color: "#8a5a00", border: "1px solid #f0d68a",
                    padding: "10px 14px", borderRadius: 10, marginBottom: 16, fontSize: 12, lineHeight: 1.55,
                  }}>
                    Your account is not bound to a store, and only a superadmin can change the estate-wide
                    defaults — so there is nothing on this page you can save. Ask a superadmin to assign your
                    account to a store.
                  </div>
                )}

                <div style={{ display: "flex" }}>
                  <button onClick={handleSave} disabled={saving || loadFailed || noEditableScope}
                    style={{ padding: "11px 40px", borderRadius: 10, border: "none", background: (saving || loadFailed || noEditableScope) ? "#a5a4f3" : "var(--mly-heading)", color: "var(--mly-on-accent)", fontWeight: 700, fontSize: 14, cursor: (saving || loadFailed || noEditableScope) ? "not-allowed" : "pointer" }}>
                    {saving ? "Saving..." : storeMode ? "Save Overrides" : "Save Settings"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreSettings;
